/**
 * EvidencePane
 *
 * Right-side panel that previews sources supporting a selected field. When a
 * field is active, it derives citations/artifacts from `requirementsById` and
 * renders a compact list plus a modal viewer for details. Shows an instructive
 * empty state when no field is selected or no sources exist.
 */
import React, { useMemo } from 'react';

import {
  EvidenceCard,
  EvidenceData,
} from '@/components/citations/EvidenceCard';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { CoverageMentionStub } from '@/services/types/coverage-types';
// no card container for minimal style
import { DataRequirement } from '@/services/types/data-requirements-types';

import { ArtifactBrowserPanel } from './ArtifactBrowserPanel';
import { type FieldSourceInfo } from './ReviewFieldsPanel';
import { hasEvidenceContext } from './utils/evidence';

// TODO: When transcript integration lands, import the live pane and remove placeholder copy

/** Props for `EvidencePane`. */
interface EvidencePaneProps {
  /** Matter id for fetching/launching citation viewers. */
  matterId: string;
  /** Currently focused field key (derives evidence list). */
  activeFieldKey?: string | null;
  /** Source info for the active field from ReviewFieldsPanel (for field-level sourcing). */
  activeFieldSource?: FieldSourceInfo | null;
  /** Data requirements map used to build citation evidence. */
  requirementsById?: Record<string, DataRequirement>;
  /** Optional: explicit label overrides for synthetic keys */
  labelOverrideByKey?: Record<string, string>;
  /** Optional: when no evidence found, prefill artifact search with this value */
  initialSearchTerm?: string;
  /** Optional: coverage-level mentions to use as evidence when requirement-derived evidence is absent */
  coverageMentions?: CoverageMentionStub[] | null;
  /** Section context determines evidence strategy */
  sectionType?: 'client' | 'vehicle' | 'insurance' | 'incident';
  /** Whether the sources pane is frozen/locked to a specific field */
  isFrozen?: boolean;
}

export const EvidencePane: React.FC<EvidencePaneProps> = ({
  matterId,
  activeFieldKey,
  activeFieldSource,
  requirementsById,
  labelOverrideByKey,
  initialSearchTerm,
  coverageMentions,
  sectionType = 'client',
  isFrozen = false,
}) => {
  const humanizeKey = (key: string): string => {
    if (!key) return '';
    const parts = key.split(':').filter((seg) => seg && !/^\d+$/.test(seg));
    const words = parts.join(' ').replaceAll('_', ' ').replaceAll('-', ' ');
    const title = words
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    // Normalize a few common acronyms
    return title
      .replace(/\bVin\b/g, 'VIN')
      .replace(/\bDob\b/g, 'DOB')
      .replace(/\bSsn\b/g, 'SSN')
      .replace(/\bLor\b/g, 'LOR');
  };

  const evidenceFromRequirement: EvidenceData[] = useMemo(() => {
    if (!activeFieldKey || !requirementsById) return [];
    const req = requirementsById[activeFieldKey];
    if (!req) return [];
    const values = req.values || [];
    return values
      .filter((v) =>
        hasEvidenceContext(
          (v as { context?: Record<string, unknown> }).context,
        ),
      )
      .map((v) => {
        const ctx = (
          v as unknown as {
            context: Record<string, unknown>;
            confidence?: number;
          }
        ).context;
        return {
          artifact_id: ctx.artifact_id,
          citation_ids: ctx.citation_ids,
          context: ctx.extraction_source,
          rationale: ctx.rationale,
          excerpts: ctx.excerpts,
          extraction_context: ctx.extraction_context,
          confidence_notes: ctx.confidence_notes,
          confidence: (v as unknown as { confidence?: number }).confidence,
        } as EvidenceData;
      });
  }, [activeFieldKey, requirementsById]);

  // Build evidence items from coverage mentions (used as a fallback when no requirement-based evidence exists)
  const evidenceFromCoverageMentions: EvidenceData[] = useMemo(() => {
    if (!coverageMentions || coverageMentions.length === 0) return [];
    return coverageMentions.map((m) => ({
      artifact_id: m.artifact_id,
      citation_ids: m.citation_ids,
      context: m.context || undefined,
      rationale: m.context || undefined,
      created_at: m.created_at,
      artifact_type: m.artifact_type,
      confidence: m.confidence ?? undefined,
    }));
  }, [coverageMentions]);

  // Build evidence from the active field's source info (field-level sourcing from ReviewFieldsPanel)
  const evidenceFromFieldSource: EvidenceData | null = useMemo(() => {
    if (!activeFieldSource?.artifactId) return null;
    return {
      artifact_id: activeFieldSource.artifactId,
      citation_ids: activeFieldSource.citationIds ?? [],
      context: activeFieldSource.isAiSuggestion
        ? 'Extracted from coverage document'
        : activeFieldSource.sourceLabel,
      rationale: activeFieldSource.sourceLabel,
      confidence: activeFieldSource.isAiSuggestion ? 0.95 : undefined,
    };
  }, [activeFieldSource]);

  // Group evidence by artifact and sort by highest confidence (DESC)
  const groupedAndSortedEvidence: EvidenceData[] = useMemo(() => {
    // Priority order for evidence:
    // 1. Field-specific source from ReviewFieldsPanel (activeFieldSource)
    // 2. Requirement-derived evidence for client fields
    // 3. Coverage mentions for vehicle/insurance/incident fields
    let baseEvidence: EvidenceData[] = [];

    // If we have field-specific source, prioritize it
    if (evidenceFromFieldSource) {
      baseEvidence = [evidenceFromFieldSource];
    } else if (
      sectionType === 'vehicle' ||
      sectionType === 'insurance' ||
      sectionType === 'incident'
    ) {
      // For vehicle/insurance/incident, show coverage mentions list
      baseEvidence = evidenceFromCoverageMentions;
    } else if (evidenceFromRequirement.length > 0) {
      // Client: show requirement evidence if available; otherwise none
      baseEvidence = evidenceFromRequirement;
    }
    if (!baseEvidence.length) return [];

    const map = new Map<string, EvidenceData & { _rationales?: string[] }>();

    baseEvidence.forEach((item, index) => {
      const key = item.artifact_id || `unknown-${index}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, {
          ...item,
          citation_ids: (item.citation_ids || []).slice(),
          _rationales: item.rationale ? [item.rationale] : [],
        });
        return;
      }

      const mergedCitations = new Set<string>([
        ...(existing.citation_ids || []),
        ...(item.citation_ids || []),
      ] as string[]);

      // Choose the highest confidence seen
      const existingConf = existing.confidence ?? -1;
      const nextConf = item.confidence ?? -1;
      const confidence = Math.max(existingConf, nextConf);

      existing.citation_ids = Array.from(mergedCitations);
      existing.confidence = confidence >= 0 ? confidence : undefined;
      if (item.rationale) existing._rationales?.push(item.rationale);
      // Prefer a non-empty context; keep earliest non-empty
      if (!existing.context && item.context) existing.context = item.context;
      if (!existing.extraction_context && item.extraction_context)
        existing.extraction_context = item.extraction_context;
      if (!existing.confidence_notes && item.confidence_notes)
        existing.confidence_notes = item.confidence_notes;
    });

    const aggregated = Array.from(map.values()).map(
      (e) =>
        ({
          ...e,
          rationale: (e._rationales || []).join(' '),
        }) as EvidenceData,
    );

    // Sort: prioritize the active field's source artifact first, then by confidence DESC
    const sourceArtifactId = activeFieldSource?.artifactId;
    return aggregated.sort((a, b) => {
      // If one matches the active field source, it goes first
      if (sourceArtifactId) {
        const aIsSource = a.artifact_id === sourceArtifactId;
        const bIsSource = b.artifact_id === sourceArtifactId;
        if (aIsSource && !bIsSource) return -1;
        if (bIsSource && !aIsSource) return 1;
      }
      const ac = a.confidence ?? -1;
      const bc = b.confidence ?? -1;
      return bc - ac;
    });
  }, [
    sectionType,
    evidenceFromRequirement,
    evidenceFromCoverageMentions,
    evidenceFromFieldSource,
    activeFieldSource?.artifactId,
  ]);

  // Reserved labels/styles removed; minimal header retained for clarity

  const sourceCount = groupedAndSortedEvidence.length;

  const activeLabel = useMemo(() => {
    if (!activeFieldKey) return undefined;
    const override = labelOverrideByKey?.[activeFieldKey];
    if (override && override.trim()) return override;
    const req = requirementsById ? requirementsById[activeFieldKey] : undefined;
    if (req?.display_name) return req.display_name;
    if (req?.name) return humanizeKey(String(req.name));
    return humanizeKey(activeFieldKey);
  }, [activeFieldKey, requirementsById, labelOverrideByKey]);

  // reserved for future derived helper state
  useMemo(() => null, []);

  return (
    <div
      className={`h-full w-full p-4 pt-0 transition-colors ${activeFieldKey ? 'bg-indigo-25/50' : ''} flex flex-col min-h-0`}
      aria-label="Evidence pane"
    >
      {/* Minimal header + sources pill */}
      <div className="pb-2 shrink-0">
        <div className="flex items-center gap-2">
          <div className="text-lg font-semibold">{activeLabel}</div>
          {sourceCount > 0 && (
            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
              {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}
            </span>
          )}
          {isFrozen && (
            <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full flex items-center gap-1">
              🔒 Locked
            </span>
          )}
        </div>
        {isFrozen && (
          <p className="text-xs text-indigo-500 mt-1">
            Click the field again to unlock
          </p>
        )}
      </div>

      <div className="space-y-3 pl-3 pr-4 flex-1 overflow-y-auto min-h-0">
        {sectionType === 'client' && sourceCount === 0 ? (
          <ArtifactBrowserPanel
            matterId={matterId}
            requirementsById={requirementsById}
            activeFieldKey={activeFieldKey}
            referenceSearchTerm={(initialSearchTerm || '').trim()}
          />
        ) : (
          <div className="space-y-4">
            {groupedAndSortedEvidence.map((evidence, index) => (
              <div
                key={(evidence.artifact_id || '') + String(index)}
                className="space-y-2"
              >
                <EvidenceCard
                  evidence={evidence}
                  matterId={matterId}
                  variant="minimal"
                />
              </div>
            ))}

            <Accordion type="single" collapsible>
              <AccordionItem value="browse-docs">
                <AccordionTrigger className="px-2">
                  Browse documents
                </AccordionTrigger>
                <AccordionContent>
                  <ArtifactBrowserPanel
                    matterId={matterId}
                    requirementsById={requirementsById}
                    activeFieldKey={activeFieldKey}
                    embedded
                    referenceSearchTerm={(initialSearchTerm || '').trim()}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );
};
